import { useAppDispatch, useAppSelector } from 'app/hooks';
import StatusHeader from 'components/common/StatusHeader';
import UseToastComponent from 'components/common/UseToast';
import ja from 'date-fns/locale/ja';
import barInfoService from 'features/BarGuideInfo/barInfoService';
import { BarInfo } from 'features/BarGuideInfo/models/IBarGuideInfo';
import { DisplayControlGroup } from 'features/DisplayControlGroup/models';
import CourseService from 'features/MenuBook/components/CopyModal/services';
import serviceMenuDetails from 'features/MenuDetail/pages/service';
import SubMenuDetailModal from 'features/MenuDetail/pages/SubMenuDetail';
import { DisplayControlGroupType, MENU_DISPLAY_TIMING } from 'features/MenuList/constants';
import { Course } from 'features/SaleType/models/Course';
import _ from 'lodash';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { Accordion, Card, Col, Form, FormControl, FormSelect, ModalProps, Row } from 'react-bootstrap';
import FormCheckInput from 'react-bootstrap/esm/FormCheckInput';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { TfiReload } from 'react-icons/tfi';
import { useHistory } from 'react-router-dom';
import { formatDate, getDateString } from 'utils';
import MenuDetailModal from '../../MenuDetail/pages/MenuDetail';
import ButtonDeleteMenu from '../components/ButtonDeleteMenu';
import ButtonEditMenu from '../components/ButtonEditMenu';
import Confirmation from '../components/ConfirmDialog';
import CustomInputDatePicker from '../components/CustomInputDatePicker';
import ExportCSV, { ExportCsvRef } from '../components/ExportCSV';
import ImportCSVModal from '../components/ImportCSVModal';
import MenuListTable from '../components/MenuListTable';

import FeatureTitleBar from 'components/common/FeatureTitleBar';
import { selectIsEditMode } from 'features/common/editModeSlice';
import {
  DISPLAY_LIMIT,
  MAIN_MENU,
  MENU_DELETE_OPTIONS,
  MENU_PROPERTIES,
  MENU_PROPERTIES_1,
  MENU_PROPERTIES_2,
  MENU_TYPES,
  SEARCH_MENU_TYPE_CONDITIONS,
  SUB_MENU,
} from '../constants';
import menuService from '../menuService';
import {
  menuListActions,
  selectClassificationOptions,
  selectIsDeleted,
  selectIsRestored,
  selectMenuDetailList,
} from '../menuSlice';
import { MyButtonRef } from '../models/ButtonProps';
import { Menu, MenuDetail, SearchMenuFilter } from '../models/menu';
import './MenuList.scss';
registerLocale('ja', ja);

function MenuList() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  //selector
  const classificationOptions = useAppSelector(selectClassificationOptions);
  const menuDetails = useAppSelector(selectMenuDetailList);
  const isDeleted = useAppSelector(selectIsDeleted);
  const isRestored = useAppSelector(selectIsRestored);
  const isEditMode = useAppSelector(selectIsEditMode);

  //ref
  const menuClassificationRef = useRef<HTMLSelectElement | null>(null);
  const checkboxesRef = useRef<Array<HTMLInputElement>>([]);
  const menuTypeCheckboxRef = useRef<Array<HTMLInputElement>>([]);
  const displayLimitCheckboxRef = useRef<Array<HTMLInputElement>>([]);
  const menuPropertyCheckboxRef = useRef<Array<HTMLInputElement>>([]);
  const menuDeletedCheckboxRef = useRef<Array<HTMLInputElement>>([]);
  const importCsvModalRef = useRef<ModalProps>(ImportCSVModal);
  const menuDetailModalRef = useRef<ModalProps>(MenuDetailModal);
  const subMenuDetailModalRef = useRef<ModalProps>(SubMenuDetailModal);
  const restoredMenuIdRef = useRef<number>();

  // button ref
  const buttonDeleteRef = useRef<MyButtonRef>(null);
  const buttonEditRef = useRef<MyButtonRef>(null);
  const exportLinkRef = useRef<ExportCsvRef>(null);

  //state ref
  const checkBoxCheckedLengthRef = useRef(0);
  const menuTypeCheckedRef = useRef({ value: 0, text: '' });
  const displayLimitCheckedRef = useRef({ value: 1, text: '' });
  const menuPropertyCheckedRef = useRef({ value: 0, text: '' });
  const menuDeletedCheckedRef = useRef({ value: 0, text: '' });
  const menuCodeRef = useRef({ value: '' });
  const menuNameRef = useRef({ value: '' });
  const menuPosRef = useRef({ value: '' });
  const menuClassificationSelectedRef = useRef({ value: '', text: '' });
  const expiredDateRef = useRef(new Date());

  //state
  const [listOfMenuDetail, setListOfMenuDetail] = useState<Array<MenuDetail>>([]);
  const [confirmDeleteMenu, setConfirmDeleteMenu] = useState(false);
  const [expiredDate, setExpiredDate] = useState(new Date());
  const [listOfMenuDetailChecked, setListOfMenuDetailChecked] = useState<Array<MenuDetail>>([]);
  const [selectedMenuDetail, setSelectedMenuDetail] = useState<MenuDetail>();

  // effect
  useEffect(() => {
    dispatch(menuListActions.fetchMenuClassification());
  }, [dispatch]);

  useEffect(() => {
    if (menuDetails && menuDetails.length > 0) {
      setListOfMenuDetail(menuDetails);
    } else {
      setListOfMenuDetail([]);
    }
    //reset refer checkbox
    resetCheckedMenu();
    checkBoxCheckedLengthRef.current = 0;
    enableDisableBtn();
  }, [menuDetails]);

  // set state of checkbox type of menu option
  const handleMenuTypeChecked = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    //reset checked box
    menuTypeCheckboxRef.current.forEach((checkbox) => {
      if (checkbox && parseInt(value) !== parseInt(checkbox.value)) {
        checkbox.checked = false;
      }
    });
    menuTypeCheckedRef.current.value = parseInt(value);
    const label = document.querySelector(`label[for=${event.currentTarget.id}]`);
    menuTypeCheckedRef.current.text = label ? label.textContent ?? '' : '';
    renderFilterSummary();
  };

  // set state of checkbox type of menu option
  const handleDisplayLimitChecked = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    //reset checked box
    displayLimitCheckboxRef.current.forEach((checkbox) => {
      if (checkbox && parseInt(value) !== parseInt(checkbox.value)) {
        checkbox.checked = false;
      }
    });
    displayLimitCheckedRef.current.value = parseInt(value);
    const label = document.querySelector(`label[for=${event.currentTarget.id}]`);
    displayLimitCheckedRef.current.text = label ? label.textContent ?? '' : '';
    renderFilterSummary();
  };

  // set state of checkbox property menu option
  const handleMenuPropertyChecked = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    //reset checked box
    menuPropertyCheckboxRef.current.forEach((checkbox) => {
      if (checkbox && parseInt(value) !== parseInt(checkbox.value)) {
        checkbox.checked = false;
      }
    });
    menuPropertyCheckedRef.current.value = parseInt(value);
    const label = document.querySelector(`label[for=${event.currentTarget.id}]`);
    menuPropertyCheckedRef.current.text = label ? label.textContent ?? '' : '';
    renderFilterSummary();
  };

  // set state of checkbox delete menu option
  const handleMenuDeletedChecked = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    //reset checked box
    menuDeletedCheckboxRef.current.forEach((checkbox) => {
      if (checkbox && parseInt(value) !== parseInt(checkbox.value)) {
        checkbox.checked = false;
      }
    });
    menuDeletedCheckedRef.current.value = parseInt(value);
    const label = document.querySelector(`label[for=${event.currentTarget.id}]`);
    menuDeletedCheckedRef.current.text = label ? label.textContent ?? '' : '';
    renderFilterSummary();
  };

  const handleMenuClassificationSelected = (event: ChangeEvent<HTMLSelectElement>) => {
    menuClassificationSelectedRef.current.value = event.target.value;
    if (menuClassificationRef.current) {
      const selectedOption = menuClassificationRef.current.options[menuClassificationRef.current.selectedIndex];
      menuClassificationSelectedRef.current.text = selectedOption.label;
    }
    renderFilterSummary();
  };

  //checkbox selected all
  const handleSelectedAll = () => {
    let count = 0;
    checkboxesRef.current.forEach((checkbox) => {
      if (checkbox) {
        checkbox.checked = true;
        count++;
      }
    });
    checkBoxCheckedLengthRef.current = count;
    enableDisableBtn();
  };

  //check box unselected all
  const handleUnSelectedAll = () => {
    let count = checkBoxCheckedLengthRef.current;
    checkboxesRef.current.forEach((checkbox) => {
      if (checkbox) {
        checkbox.checked = false;
        if (count > 0) count--;
      }
    });
    checkBoxCheckedLengthRef.current = count;
    enableDisableBtn();
  };

  // set state of selected expired date
  const handleChangeExpiredDate = (date: Date) => {
    expiredDateRef.current = date;
    setExpiredDate(date);
    renderFilterSummary();
  };

  //deleted menu on click button
  const handleBtnDeleteMenu = async () => {
    const menuIds = new Array<number>();
    checkboxesRef.current.forEach((checkbox) => {
      if (checkbox && checkbox.checked) {
        menuIds.push(parseInt(checkbox.id));
      }
    });
    const uniq_menu_ids = _.uniq(menuIds);
    const deleted_menu_details = _.filter(listOfMenuDetail, (obj) => _.includes(uniq_menu_ids, obj.id));
    const arrayOfMenuIDs = _.flatMap(deleted_menu_details, (obj) => _.get(obj, 'menu.id'));
    await menuService.deleteMenu(_.uniq(arrayOfMenuIDs)).then((response) => {
      if (response.status === 'success') {
        dispatch(menuListActions.deleteMenuSuccess(true));
        handleSearchMenuDetail(undefined);
      } else {
        console.error('Failed to delete menu:', response.message);
      }
    });
  };

  const [searchMenuContidion, setSearchMenuCondition] = useState<number>(1);
  const [searchMenuDisplayTiming, setMenuDisplayTiming] = useState<number>(0);
  const [searchDisplayControlGroupTiming, setSearchDisplayControlGroupTiming] = useState<number | null>(null);
  const [searchDisplayControlGroup, setSearchDisplayControlGroup] = useState<number | null>(null);
  const [searchBarGuidence, setSearchBarGuidence] = useState<number | null>(null);
  const [searchCourse, setSearchCourse] = useState<number | null>();
  const [searchOrderLimit, setSearchOrderLimit] = useState<number | null>(null);
  //search menu detail on click button
  const handleSearchMenuDetail = (event: FormEvent<HTMLFormElement> | undefined) => {
    const searchObj = {
      menu_classification: menuClassificationSelectedRef.current.value,
      menu_type_conditions: menuTypeCheckedRef.current.value, // 0: '全て表示', 1: '通常メニューのみ表示', 2: 'サブメニューのみ表示'
      deleted_data: menuDeletedCheckedRef.current.value, // 0 is hide_deleted_data, 1 is display_include_deleted_data, 2 is show_deleted_data_only
      menu_name: menuNameRef.current.value,
      menu_code: menuCodeRef.current.value,
      menu_property: menuPropertyCheckedRef.current.value, // 0 show_all, 1 lunch, 2 set_meal, 3 age_limit, 4 call_the_clerk, 5 coupon, 6 service
      expired_date: getDateString(expiredDateRef.current),
      menu_pos: menuPosRef.current.value,
      display_limit: displayLimitCheckedRef.current.value, // 0 is all, 1 is 100 items, 2 is none
      search_menu_condition: searchMenuContidion,
      search_display_timing: searchMenuDisplayTiming,
      search_display_control_group_timing: searchDisplayControlGroupTiming,
      search_display_control_group: searchDisplayControlGroup,
      search_bar_guidance: searchBarGuidence,
      search_course: searchCourse,
      search_order_limit: searchOrderLimit,
    } as SearchMenuFilter;
    if (event) {
      event.preventDefault();
    }
    if (menuDetailModalRef.current && typeof menuDetailModalRef.current.onReset === 'function') {
      menuDetailModalRef.current.onReset();
    }
    if (subMenuDetailModalRef.current && typeof subMenuDetailModalRef.current.onReset === 'function') {
      subMenuDetailModalRef.current.onReset();
    }
    dispatch(menuListActions.fetchListOfMenuDetail(searchObj));
  };

  //checked menu items on checked box
  const handleCheckedMenu = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked === true) {
      checkBoxCheckedLengthRef.current = ++checkBoxCheckedLengthRef.current;
    } else if (event.target.checked === false) {
      checkBoxCheckedLengthRef.current = --checkBoxCheckedLengthRef.current;
    }
    enableDisableBtn();
    //setStateCheckboxRef(checkboxesRef);
  };

  //const reset btn state
  const enableDisableBtn = () => {
    if (checkBoxCheckedLengthRef.current === 0) {
      buttonDeleteRef.current?.disable();
      buttonEditRef.current?.disable();
    } else if (checkBoxCheckedLengthRef.current === 1) {
      buttonDeleteRef.current?.enable();
      buttonEditRef.current?.enable();
    } else if (checkBoxCheckedLengthRef.current > 1) {
      buttonDeleteRef.current?.enable();
      buttonEditRef.current?.disable();
    }
  };

  // reset checked menu items
  const resetCheckedMenu = () => {
    //reset refer checkbox
    checkboxesRef.current.forEach((checkbox) => {
      if (checkbox) checkbox.checked = false;
    });
  };

  // handel import csv btn
  const handleImportCsvBtn = () => {
    importCsvModalRef.current.openModal();
  };

  const handleExportCsv = () => {
    const menuIds = new Array<number>();
    checkboxesRef.current.forEach((checkbox) => {
      if (checkbox && checkbox.checked) {
        menuIds.push(parseInt(checkbox.id));
      }
    });
    const lst_detail = listOfMenuDetail.filter((menu_detail: MenuDetail) => menuIds.includes(menu_detail.id));
    setListOfMenuDetailChecked(lst_detail);
  };

  //handleOpenMenuDetailModal
  const handleOpenMenuDetailModal = () => {
    const checkedBoxes = checkboxesRef.current.filter((checkbox) => checkbox && checkbox.checked === true);
    if (checkedBoxes && checkedBoxes.length > 0) {
      const menuDetail = listOfMenuDetail.find((item) => item.id === parseInt(checkedBoxes[0].id));
      setSelectedMenuDetail(menuDetail);
    }
    buttonEditRef.current?.blur();
  };

  const initFilterSummary = () => {
    menuClassificationSelectedRef.current.text = t('MenuList.Filter.selection');
    const menuTypeChecked = MENU_TYPES.find((x) => x.value === menuTypeCheckedRef.current.value);
    menuTypeCheckedRef.current.text = menuTypeChecked?.key ?? '';
    const menuPropertyChecked = MENU_PROPERTIES.find((x) => x.value === menuPropertyCheckedRef.current.value);
    menuPropertyCheckedRef.current.text = menuPropertyChecked?.key ?? '';
    const menuDeleteChecked = MENU_DELETE_OPTIONS.find((x) => x.value === menuDeletedCheckedRef.current.value);
    menuDeletedCheckedRef.current.text = menuDeleteChecked?.key ?? '';
    renderFilterSummary();
  };

  const [textBarGuidance, setTextBarGuidance] = useState<string>(t('MenuList.Filter.no_selection'));
  const [textCourse, setTextCourse] = useState<string>(t('MenuList.Filter.no_selection'));
  const [textOrderLimit, setTextOrderLimit] = useState<string>(t('MenuList.Filter.no_selection'));
  const [textDisplayTiming, setTextDisplayTiming] = useState<string>('常時');
  const [textDisplayControlGroup, setTextDisplayControlGroup] = useState<string>(t('MenuList.Filter.no_selection'));
  const [textDisplayControlGroupTiming, setTextDisplayControlGroupTiming] = useState<string>(
    t('MenuList.Filter.no_selection')
  );

  const renderFilterSummary = () => {
    const menu_classification = document.querySelector('#summary_menu_classification');
    if (menu_classification) {
      menu_classification.innerHTML = menuClassificationSelectedRef.current.text;
    }
    const menu_type = document.querySelector('#summary_menu_type');
    if (menu_type) {
      menu_type.innerHTML = menuTypeCheckedRef.current.text;
    }
    const summary_menu_property = document.querySelector('#summary_menu_property');
    if (summary_menu_property) {
      summary_menu_property.innerHTML = menuPropertyCheckedRef.current.text;
    }
    const summary_menu_deleted = document.querySelector('#summary_menu_deleted');
    if (summary_menu_deleted) {
      summary_menu_deleted.innerHTML = menuDeletedCheckedRef.current.text;
    }
    const summary_menu_name = document.querySelector('#summary_menu_name');
    if (summary_menu_name) {
      summary_menu_name.innerHTML = menuNameRef.current.value;
    }
    const summary_menu_code = document.querySelector('#summary_menu_code');
    if (summary_menu_code) {
      summary_menu_code.innerHTML = menuCodeRef.current.value;
    }
    const summary_date_expired = document.querySelector('#summary_date_expired');
    if (summary_date_expired) {
      summary_date_expired.innerHTML = formatDate(expiredDateRef.current);
    }

    const summary_menu_pos = document.querySelector('#summary_menu_pos');
    if (summary_menu_pos) {
      summary_menu_pos.innerHTML = menuPosRef.current.value;
    }

    let display_limit = document.querySelector('#summary_display_limit');
    if (display_limit) {
      display_limit.innerHTML =
        displayLimitCheckedRef.current.text || DISPLAY_LIMIT.find((x) => x.value === 1)?.key || 'あり（100件のみ表示）';
    }

    const summary_search_display_timing = document.querySelector('#summary_search_display_timing');
    const summary_search_display_control_group = document.querySelector('#summary_search_display_control_group');
    const summary_search_display_control_group_timing = document.querySelector(
      '#summary_search_display_control_group_timing'
    );

    if (summary_search_display_timing) {
      summary_search_display_timing.innerHTML =
        '表示タイミング：' + MENU_DISPLAY_TIMING.find((x) => x.value === searchMenuDisplayTiming)?.key;
    }
    if (summary_search_display_control_group) {
      summary_search_display_control_group.innerHTML =
        '表示制御　グループ：' +
        (searchDisplayControlGroup ? searchDisplayControlGroup : t('MenuList.Filter.no_selection'));
    }
    if (summary_search_display_control_group_timing) {
      summary_search_display_control_group_timing.innerHTML =
        '表示制御　グループ：' +
        (searchDisplayControlGroup ? searchDisplayControlGroup : t('MenuList.Filter.no_selection'));
    }
  };

  const menuDetailCallBack = (objCallBack: {} | undefined) => {
    if (listOfMenuDetail.length > 0) {
      const menu = objCallBack ? (objCallBack as Menu) : undefined;
      if (menu) {
        const updatedItems = [...listOfMenuDetail];
        const index = updatedItems.findIndex((item) => item.menu.id === menu.id);
        updatedItems[index] = {
          ...updatedItems[index],
          menu: {
            ...updatedItems[index].menu,
            note: menu.note,
            img: menu.img,
            menu_properties: menu.menu_properties,
            coupon_classification: menu.coupon_classification,
            display_timing: menu.display_timing,
            display_name: menu.display_name,
            display_name_en: menu.display_name_en,
            display_name_ja: menu.display_name_ja,
            display_name_vi: menu.display_name_vi,
            display_name_ko: menu.display_name_ko,
            display_name_zh_hant: menu.display_name_zh_hant,
            display_name_zh_hans: menu.display_name_zh_hans,
            explanation: menu.explanation,
            explanation_en: menu.explanation_en,
            explanation_ja: menu.explanation_ja,
            explanation_vi: menu.explanation_vi,
            explanation_ko: menu.explanation_ko,
            explanation_zh_hant: menu.explanation_zh_hant,
            explanation_zh_hans: menu.explanation_zh_hans,
            note1: menu.note1,
            note1_en: menu.note1_en,
            note1_ja: menu.note1_ja,
            note1_vi: menu.note1_vi,
            note1_ko: menu.note1_ko,
            note1_zh_hant: menu.note1_zh_hant,
            note1_zh_hans: menu.note1_zh_hans,
            note2: menu.note2,
            note2_en: menu.note2_en,
            note2_ja: menu.note2_ja,
            note2_vi: menu.note2_vi,
            note2_ko: menu.note2_ko,
            note2_zh_hant: menu.note2_zh_hant,
            note2_zh_hans: menu.note2_zh_hans,
          },
        };
        setListOfMenuDetail(updatedItems);
      }
    }
  };

  const handleRestoreMenu = async (id: number) => {
    if (id) {
      dispatch(menuListActions.restoreMenu(id));
      restoredMenuIdRef.current = id;
      handleSearchMenuDetail(undefined);
    }
  };

  // effect
  useEffect(() => {
    if (isDeleted) {
      const menuIds = new Array<number>();
      checkboxesRef.current.forEach((checkbox) => {
        if (checkbox && checkbox.checked) {
          menuIds.push(parseInt(checkbox.id));
        }
      });
      const uniq_menu_ids = _.uniq(menuIds);
      const deleted_menus = listOfMenuDetail.filter((menu_detail: MenuDetail) =>
        uniq_menu_ids.includes(menu_detail.id)
      );
      const lst_detail = listOfMenuDetail.map((menu_detail) => ({
        ...menu_detail,
        delete_flg: deleted_menus.some((obj) => obj.id === menu_detail.id) ? true : menu_detail.delete_flg,
      }));
      setConfirmDeleteMenu(false);
      setListOfMenuDetail(lst_detail);
      //reset refer checkbox
      resetCheckedMenu();
      //set checkbox checked length
      checkBoxCheckedLengthRef.current = checkBoxCheckedLengthRef.current - uniq_menu_ids.length;
      enableDisableBtn();
      dispatch(menuListActions.deleteMenuSuccess(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleted]);

  // effect
  useEffect(() => {
    if (isRestored && restoredMenuIdRef.current !== undefined) {
      const lst_detail = listOfMenuDetail.map((menu_detail) => ({
        ...menu_detail,
        delete_flg: menu_detail.menu.id === restoredMenuIdRef.current ? false : menu_detail.delete_flg,
      }));
      setListOfMenuDetail(lst_detail);
      dispatch(menuListActions.restoreMenuSuccess(false));
    }
    restoredMenuIdRef.current = undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRestored]);

  // effect
  useEffect(() => {
    initFilterSummary();
    handleSearchMenuDetail(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // effect
  useEffect(() => {
    if (selectedMenuDetail !== undefined) {
      const expired_date = getDateString(expiredDateRef.current);
      if (selectedMenuDetail?.menu.type === MAIN_MENU) {
        menuDetailModalRef.current.openModal(selectedMenuDetail, expired_date);
      } else if (selectedMenuDetail?.menu.type === SUB_MENU) {
        subMenuDetailModalRef.current.openModal(selectedMenuDetail, expired_date);
      } else {
        menuDetailModalRef.current.openModal(selectedMenuDetail, expired_date);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenuDetail?.id]);

  // effect
  useEffect(() => {
    if (listOfMenuDetailChecked.length > 0) {
      if (exportLinkRef.current) {
        exportLinkRef.current?.onClick();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfMenuDetailChecked]);

  const handleOnCloseModel = () => {
    setSelectedMenuDetail(undefined);
    handleSearchMenuDetail(undefined);
  };

  // load bar guidance menu
  const [listGuidances, setListGuidance] = useState<BarInfo[]>([]);
  useEffect(() => {
    barInfoService.getBarInformationList(0).then((res) => {
      if (res.data) {
        setListGuidance(res.data);
      }
    });
  }, []);
  // load courses
  const [listCourses, setListCourses] = useState<Course[]>([]);
  useEffect(() => {
    CourseService.getCourses().then((res) => {
      if (res.data) {
        setListCourses(res.data);
      }
    });
  }, []);

  //load order limit
  const [orderLimit, setOrderLimit] = useState<{ key: string; value: string }[]>([]);
  useEffect(() => {
    menuService.getOrderLimit().then((res) => {
      if (res.data) {
        setOrderLimit(res.data);
      }
    });
  }, []);

  function handleSearchDisplayControlGroup(event: ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    setSearchDisplayControlGroup(Number(value) === 0 ? null : Number(value));
    setTextDisplayControlGroup(event.target.options[event.target.selectedIndex].text);
  }

  function handleSearchDisplayControlGroupTiming(event: ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    setSearchDisplayControlGroupTiming(Number(value) === 0 ? null : Number(value));
    setTextDisplayControlGroupTiming(event.target.options[event.target.selectedIndex].text);
    renderFilterSummary();
  }

  function handleChangeMenuDisplayTiming(event: ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    setMenuDisplayTiming(Number(value));
    setTextDisplayTiming(event.target.options[event.target.selectedIndex].text);
  }

  const [listDisplayControlGroup, setDisplayControlGroup] = useState<DisplayControlGroupType[]>([]);
  useEffect(() => {
    serviceMenuDetails.getDisplayControlGroups().then((res) => {
      const data: DisplayControlGroupType[] = res.data
        .filter((item: DisplayControlGroup) => !item.delete_flg)
        .map((item: DisplayControlGroup) => ({
          value: item.id,
          key: item.name,
          display_name: item.display_name,
        }));
      setDisplayControlGroup(data);
    });
  }, []); // Add an empty dependency array here

  function handleSearchMenuCondition(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setSearchMenuCondition(Number(value));
    renderFilterSummary();
  }

  function handleChangSearchBarGuidance(event: ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    setSearchBarGuidence(Number(value) === 0 ? null : Number(value));
    setTextBarGuidance(event.target.options[event.target.selectedIndex].text);
  }

  function handleChangeSearchCourse(event: ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    setSearchCourse(Number(value) === 0 ? null : Number(value));
    setTextCourse(event.target.options[event.target.selectedIndex].text);
  }

  function handleChangeSearchOrderLimit(event: ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    setSearchOrderLimit(Number(value) === 0 ? null : Number(value));
    setTextOrderLimit(event.target.options[event.target.selectedIndex].text);
  }

  return (
    <>
      {confirmDeleteMenu && (
        <Confirmation
          okLabel={t('MenuList.DialogConfirm.btn_save')}
          cancelLabel={t('MenuList.DialogConfirm.btn_cancel')}
          title={t('MenuList.DialogConfirm.title')}
          confirmationMessage={t('MenuList.DialogConfirm.message')}
          onHide={() => setConfirmDeleteMenu(false)}
          proceed={handleBtnDeleteMenu}
          enableEscape={false}
        />
      )}
      <ExportCSV data={listOfMenuDetailChecked ?? []} ref={exportLinkRef} />
      <ImportCSVModal ref={importCsvModalRef} />
      <MenuDetailModal
        ref={menuDetailModalRef}
        menuCallBack={menuDetailCallBack}
        onMenuModalClose={handleOnCloseModel}
      />
      <UseToastComponent />
      <div className="page-wrapper">
        <div className="page-header">
          <StatusHeader onAction={() => history.push('/')} />
        </div>
        <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
          <div className="page-content">
            <div className="row">
              <div className="col-xs-12 col-sm-12 col-lg-12">
                <Card className="mb-2">
                  <Card.Body>
                    <div className="row">
                      <div className="col-xs-10 col-sm-4 col-md-3">
                        <FeatureTitleBar title={t('MenuList.Filter.menu_list')} />
                      </div>
                    </div>
                    <Accordion className="mb-3" defaultActiveKey={['0', '1', '2']} alwaysOpen>
                      <Accordion.Item eventKey="0" className="movement-information">
                        <Accordion.Header>{t('MenuList.Filter.filter_condition')} </Accordion.Header>
                        <Accordion.Body>
                          <Form onSubmit={handleSearchMenuDetail}>
                            <div className="row">
                              <div className="col-xs-12 col-sm-12 col-md-8 col-lg-8">
                                <div className="row mb-3 g-2 align-items-center">
                                  <div className="col-auto">
                                    <label htmlFor="menu_classification" className="col-form-label">
                                      {t('MenuList.Filter.menu_classification')}
                                    </label>
                                  </div>
                                  <div className="col-auto">
                                    <div>
                                      <FormSelect
                                        onChange={handleMenuClassificationSelected}
                                        ref={menuClassificationRef}
                                        defaultValue={menuClassificationSelectedRef.current.value}
                                        id="menu_classification"
                                      >
                                        <option value="">{t('MenuList.Filter.selection')}</option>
                                        {classificationOptions.map((classification, index) => {
                                          return (
                                            <option key={index} value={classification.value}>
                                              {classification.label}
                                            </option>
                                          );
                                        })}
                                      </FormSelect>
                                    </div>
                                  </div>
                                </div>
                                <div className="row g-2 mb-3"></div>
                                <div className="row g-2 mb-3 align-items-center">
                                  <div className="col-auto">
                                    <label className="col-form-label">
                                      {t('MenuList.Filter.search_menu_type_condition')}
                                    </label>
                                  </div>
                                  <div className="col-auto">
                                    {SEARCH_MENU_TYPE_CONDITIONS.map((menu, index) => {
                                      return (
                                        <div className="form-check form-check-inline" key={index}>
                                          <FormCheckInput
                                            type="radio"
                                            defaultChecked={
                                              menu.value === menuTypeCheckedRef.current.value ? true : false
                                            }
                                            onChange={handleMenuTypeChecked}
                                            ref={(element: HTMLInputElement) => {
                                              menuTypeCheckboxRef.current[menu.value] = element;
                                            }}
                                            value={menu.value}
                                            id={'menu_type_' + menu.value}
                                          ></FormCheckInput>
                                          <label className="form-check-label" htmlFor={'menu_type_' + menu.value}>
                                            {menu.key}
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="row mb-3">
                                  <div className="col">
                                    <div className="row mb-3 g-2 align-items-center">
                                      <div className="col-auto">
                                        <label htmlFor="menu_name" className="col-form-label">
                                          {t('MenuList.Filter.menu_name')}
                                        </label>
                                      </div>
                                      <div className="col-auto">
                                        <div>
                                          <FormControl
                                            id="menu_name"
                                            type="text"
                                            onChange={(e) => {
                                              menuNameRef.current.value = e.target.value;
                                              renderFilterSummary();
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="row mb-3">
                                  <div className="col">
                                    <div className="row mb-3 g-2 align-items-center">
                                      <div className="col-auto">
                                        <label htmlFor="menu_code" className="col-form-label">
                                          {t('MenuList.Filter.menu_code')}
                                        </label>
                                      </div>
                                      <div className="col-auto">
                                        <div>
                                          <FormControl
                                            id="menu_code"
                                            type="text"
                                            onChange={(e) => {
                                              menuCodeRef.current.value = e.target.value;
                                              renderFilterSummary();
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="row mb-3"></div>
                                <div className="row mb-3">
                                  <div className="col-auto">
                                    <label className="col-form-label">{t('MenuList.Filter.properties')}</label>
                                  </div>
                                  <div className="col-sm-2 col-xs-6 ">
                                    <div className="row mb-3 g-2 align-items-center">
                                      <div className="col-auto">
                                        {MENU_PROPERTIES_1.map((menu, index) => {
                                          return (
                                            <div className="form-check" key={index}>
                                              <FormCheckInput
                                                type="radio"
                                                id={'menu_property1_' + menu.value}
                                                defaultChecked={menu.value === 0 ? true : false}
                                                onChange={handleMenuPropertyChecked}
                                                ref={(element: HTMLInputElement) => {
                                                  menuPropertyCheckboxRef.current[menu.value] = element;
                                                }}
                                                value={menu.value}
                                              ></FormCheckInput>
                                              <label
                                                className="form-check-label"
                                                htmlFor={'menu_property1_' + menu.value}
                                              >
                                                {menu.key}
                                              </label>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-sm-2 col-xs-6">
                                    <div className="row mb-3 g-2 align-items-center">
                                      <div className="col-auto">
                                        {MENU_PROPERTIES_2.map((menu, index) => {
                                          return (
                                            <div className="form-check" key={index}>
                                              <FormCheckInput
                                                type="radio"
                                                id={'menu_property2_' + menu.value}
                                                value={menu.value}
                                                defaultChecked={menu.value === 0 ? true : false}
                                                onChange={handleMenuPropertyChecked}
                                                ref={(element: HTMLInputElement) => {
                                                  menuPropertyCheckboxRef.current[menu.value] = element;
                                                }}
                                              ></FormCheckInput>
                                              <label
                                                className="form-check-label"
                                                htmlFor={'menu_property2_' + menu.value}
                                              >
                                                {menu.key}
                                              </label>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="row mb-3"></div>
                                <div className="row mb-3"></div>
                                <div className="row mb-3">
                                  <div className="col-md-2" style={{ paddingTop: '30px' }}>
                                    {t('MenuList.Filter.display_timing')}
                                  </div>
                                  <div className="col-md-3">
                                    <div className="form-check">
                                      <FormCheckInput
                                        type="radio"
                                        id={'search_menu_condition_1'}
                                        onChange={handleSearchMenuCondition}
                                        name="search_menu_condition"
                                        defaultChecked
                                        value={1}
                                      ></FormCheckInput>
                                      <label className="form-check-label" htmlFor={'search_menu_condition_1'}>
                                        {t('MenuList.Filter.display_timing_menu_search')}
                                      </label>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="row">
                                      <div className="col-4">
                                        <label htmlFor="menu_display_timing" className="col-form-label">
                                          {t('MenuList.Filter.control_condition')}
                                        </label>
                                      </div>
                                      <div className="col-6">
                                        <Form.Select onChange={handleChangeMenuDisplayTiming}>
                                          <option value={0}>{t('MenuList.Filter.no_selection')}</option>
                                          {MENU_DISPLAY_TIMING.map((condition, index) => {
                                            return (
                                              <option key={index} value={condition.value}>
                                                {condition.key}
                                              </option>
                                            );
                                          })}
                                        </Form.Select>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="row">
                                      <div className="col-4">
                                        <label htmlFor="display_control_group_1" className="col-form-label">
                                          <div
                                            dangerouslySetInnerHTML={{
                                              __html: t('MenuList.Filter.display_control_group'),
                                            }}
                                          ></div>
                                        </label>
                                      </div>
                                      <div className="col-6">
                                        <FormSelect
                                          onChange={handleSearchDisplayControlGroupTiming}
                                          id="display_control_group_1"
                                        >
                                          <option value="">{t('MenuList.Filter.no_selection')}</option>
                                          {listDisplayControlGroup.map((controlGroup, index) => {
                                            return (
                                              <option key={index} value={controlGroup.value}>
                                                {controlGroup.key}
                                              </option>
                                            );
                                          })}
                                        </FormSelect>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-2"></div>
                                  <div className="col-md-3">
                                    <div className="form-check">
                                      <FormCheckInput
                                        type="radio"
                                        id={'search_menu_condition_2'}
                                        onChange={handleSearchMenuCondition}
                                        name="search_menu_condition"
                                        value={2}
                                      ></FormCheckInput>
                                      <label className="form-check-label" htmlFor={'search_menu_condition_2'}>
                                        {t('MenuList.Filter.display_condition_menu_search')}
                                      </label>
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="row">
                                      <div className="col-4">
                                        <label htmlFor="display_control_group_2" className="col-form-label">
                                          <div
                                            dangerouslySetInnerHTML={{
                                              __html: t('MenuList.Filter.display_control_group'),
                                            }}
                                          ></div>
                                        </label>
                                      </div>
                                      <div className="col-6">
                                        <FormSelect
                                          onChange={handleSearchDisplayControlGroup}
                                          id="display_control_group_2"
                                        >
                                          <option value="">{t('MenuList.Filter.no_selection')}</option>
                                          {listDisplayControlGroup.map((controlGroup, index) => {
                                            return (
                                              <option key={index} value={controlGroup.value}>
                                                {controlGroup.key}
                                              </option>
                                            );
                                          })}
                                        </FormSelect>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-xs-12 col-sm-12 col-md-4 col-lg-4">
                                <div className="row mb-3">
                                  <div className="col-auto">{t('MenuList.Filter.bar_guidance_menu')}</div>
                                </div>
                                <div className="row mb-3">
                                  <div className="col-1"></div>
                                  <div className="col-auto">{t('MenuList.Filter.guidance_name')}</div>
                                  <div className="col-5">
                                    <FormSelect onChange={handleChangSearchBarGuidance} id="bar_guidance">
                                      <option value="">{t('MenuList.Filter.no_selection')}</option>
                                      {listGuidances.map((bargui, index) => {
                                        return (
                                          <option key={index} value={bargui.id}>
                                            {bargui.name}
                                          </option>
                                        );
                                      })}
                                    </FormSelect>
                                  </div>
                                </div>

                                <div className="row mb-3">
                                  <div className="col-auto">{t('MenuList.Filter.course_target_menu')}</div>
                                </div>
                                <div className="row mb-3">
                                  <div className="col-1"></div>
                                  <div className="col-auto">コース名：</div>
                                  <div className="col-5">
                                    <FormSelect onChange={handleChangeSearchCourse} id="courses">
                                      <option value="">{t('MenuList.Filter.no_selection')}</option>
                                      {listCourses.map((course, index) => {
                                        return (
                                          <option key={index} value={course.id}>
                                            {course.name}
                                          </option>
                                        );
                                      })}
                                    </FormSelect>
                                  </div>
                                </div>

                                <div className="row mb-3">
                                  <div className="col-auto">{t('MenuList.Filter.course_set_condition_menu')}</div>
                                </div>
                                <div className="row mb-3">
                                  <div className="col-1"></div>
                                  <div className="col-auto">コース名：</div>
                                  <div className="col-5">
                                    <FormSelect onChange={handleChangeSearchOrderLimit} id="order_limit">
                                      <option value="">{t('MenuList.Filter.no_selection')}</option>
                                      {orderLimit.map((order, index) => {
                                        return (
                                          <option key={index} value={order.key}>
                                            {order.value}
                                          </option>
                                        );
                                      })}
                                    </FormSelect>
                                  </div>
                                </div>

                                <div>{t('MenuList.Filter.deleted_data')}</div>
                                <div className="ms-3 mb-3">
                                  {MENU_DELETE_OPTIONS.map((opt, index) => {
                                    return (
                                      <div className="form-check" key={index}>
                                        <FormCheckInput
                                          className="form-check-input"
                                          type="radio"
                                          defaultChecked={opt.value === 0 ? true : false}
                                          onChange={handleMenuDeletedChecked}
                                          ref={(element: HTMLInputElement) => {
                                            menuDeletedCheckboxRef.current[opt.value] = element;
                                          }}
                                          value={opt.value}
                                          id={'menu_deleted_' + opt.value}
                                        ></FormCheckInput>
                                        <label className="form-check-label" htmlFor={'menu_deleted_' + opt.value}>
                                          {opt.key}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>

                                <div className="col-sm-12 col-xs-12 mb-3">
                                  <fieldset className="border rounded-4 p-3 mb-3">
                                    <legend className="float-none w-auto px-3" style={{ fontSize: 'inherit' }}>
                                      {t('MenuList.Filter.related_cash_register_information')}
                                    </legend>
                                    <div className="row">
                                      <div className="col-8">
                                        <div className="align-items-center">
                                          <div>
                                            {t(
                                              'MenuList.Filter.extract_the_menu_from_the_related_cash_register_information'
                                            )}
                                          </div>
                                        </div>
                                        <div className="row mb-3">
                                          <div className="col-4">
                                            <label htmlFor="menu_name" className="col-form-label">
                                              {t('MenuList.Filter.date_of_expires')}：
                                            </label>
                                          </div>
                                          <div className="col-8">
                                            <div>
                                              <DatePicker
                                                selected={expiredDate}
                                                onChange={handleChangeExpiredDate}
                                                customInput={<CustomInputDatePicker />}
                                                dateFormat="yyyy/MM/dd"
                                                locale="ja"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        <div className="row">
                                          <div className="col-4">
                                            <label htmlFor="menu_pos" className="col-form-label">
                                              {t('MenuList.Filter.pos')}
                                            </label>
                                          </div>
                                          <div className="col-8">
                                            <div>
                                              <FormControl
                                                id="menu_pos"
                                                type="text"
                                                onChange={(e) => {
                                                  menuPosRef.current.value = e.target.value;
                                                  renderFilterSummary();
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </fieldset>
                                </div>

                                <div className="row">
                                  <div className="row mb-3 g-2 align-items-center">
                                    <div className="col-auto">
                                      <label className="col-form-label">{t('MenuList.Filter.display_limit')}</label>
                                    </div>
                                    <div className="col-auto">
                                      {DISPLAY_LIMIT.map((display_limit, index) => {
                                        return (
                                          <div className="form-check form-check-inline" key={index}>
                                            <FormCheckInput
                                              type="radio"
                                              defaultChecked={
                                                display_limit.value === displayLimitCheckedRef.current.value
                                                  ? true
                                                  : false
                                              }
                                              onChange={handleDisplayLimitChecked}
                                              ref={(element: HTMLInputElement) => {
                                                displayLimitCheckboxRef.current[display_limit.value] = element;
                                              }}
                                              value={display_limit.value}
                                              id={'display_limit_' + display_limit.value}
                                            ></FormCheckInput>
                                            <label
                                              className="form-check-label"
                                              htmlFor={'display_limit_' + display_limit.value}
                                            >
                                              {display_limit.key}
                                            </label>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12">
                                <button type="submit" className="btn btn-primary float-end">
                                  {t('MenuList.Filter.btn_search')}
                                </button>
                              </div>
                            </div>
                          </Form>
                        </Accordion.Body>
                        <div className="accordion-body accordion-summary">
                          <div className="row">
                            <div className="col">
                              <div className="row mb-3 g-2 align-items-center">
                                <div className="col-auto">
                                  <label htmlFor="menu_classification" className="col-form-label">
                                    {t('MenuList.Filter.menu_classification')}
                                  </label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_menu_classification"></div>
                                </div>
                              </div>
                              <div className="row g-2 mb-3 align-items-center">
                                <div className="col-auto">
                                  <label className="col-form-label">
                                    {t('MenuList.Filter.search_menu_type_condition')}
                                  </label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_menu_type"></div>
                                </div>
                              </div>
                              <div className="row g-2 mb-3 align-items-center">
                                <div className="col-auto">
                                  <label className="col-form-label">{t('MenuList.Filter.menu_name')}</label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_menu_name"></div>
                                </div>
                                <div className="col-auto offset-md-3">
                                  <label className="col-form-label">{t('MenuList.Filter.menu_code')}</label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_menu_code"></div>
                                </div>
                              </div>
                              <div className="row g-2 mb-3 align-items-center">
                                <div className="col-auto">
                                  <label className="col-form-label">{t('MenuList.Filter.properties')}</label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_menu_property"></div>
                                </div>
                              </div>

                              <div className="row">
                                <div className="col-auto">{t('MenuList.Filter.display_timing')}</div>
                                <div className="col-auto">
                                  {searchMenuContidion === 1 ? (
                                    <div>{t('MenuList.Filter.display_timing_menu_search')}</div>
                                  ) : (
                                    <div>{t('MenuList.Filter.display_condition_menu_search')}</div>
                                  )}
                                </div>
                                {searchMenuContidion === 1 && (
                                  <div className="col-auto">
                                    <div>
                                      {t('MenuList.Filter.control_condition')}
                                      {textDisplayTiming}
                                    </div>
                                  </div>
                                )}

                                {searchMenuContidion === 1 && (
                                  <div className="col-auto">
                                    <div>表示制御&nbsp; グループ：{textDisplayControlGroupTiming}</div>
                                  </div>
                                )}

                                {searchMenuContidion === 2 && (
                                  <div className="col-auto">
                                    <div>表示制御&nbsp; グループ：{textDisplayControlGroup}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col">
                              <div className="row mb-3 ">
                                <div className="col-md-12">
                                  {t('MenuList.Filter.bar_guidance_menu')}
                                  <span className="offset-md-1">
                                    {t('MenuList.Filter.guidance_name')} {textBarGuidance}
                                  </span>
                                </div>
                              </div>

                              <div className="row mb-3 ">
                                <div className="col-md-12">
                                  {t('MenuList.Filter.course_target_menu')}
                                  <span className="offset-md-1">コース名：{textCourse}</span>
                                </div>
                              </div>
                              <div className="row mb-3 ">
                                <div className="col-md-12">
                                  {t('MenuList.Filter.course_set_condition_menu')}
                                  <span className="offset-md-1">コース名：{textOrderLimit}</span>
                                </div>
                              </div>
                              <div className="row mb-3 g-2 align-items-center">
                                <div className="col-auto">
                                  <label className="col-form-label">{t('MenuList.Filter.deleted_data')}</label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_menu_deleted"></div>
                                </div>
                              </div>

                              <div className="row g-2 mb-3 align-items-center">
                                <div className="col-auto">
                                  <label className="col-form-label">
                                    {t('MenuList.Filter.date_of_expires')}&nbsp;:&nbsp;&nbsp;
                                  </label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_date_expired"></div>
                                </div>
                              </div>
                              <div className="row g-2 mb-3 align-items-center">
                                <div className="col-auto">
                                  <label className="col-form-label">
                                    {t('MenuList.Filter.pos')}&nbsp;:&nbsp;&nbsp;
                                  </label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_menu_pos"></div>
                                </div>
                              </div>

                              <div className="row g-2 mb-3 align-items-center">
                                <div className="col-auto">
                                  <label className="col-form-label">{t('MenuList.Filter.display_limit')}</label>
                                </div>
                                <div className="col-auto">
                                  <div id="summary_display_limit"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Accordion.Item>
                    </Accordion>
                  </Card.Body>
                </Card>
              </div>
            </div>
            <Card className="card border-0">
              <Row className="mb-2">
                <Col sm={2} lg={2}>
                  <Form onSubmit={handleSearchMenuDetail}>
                    <div className="d-inline">
                      <button
                        type="button"
                        onClick={handleSelectedAll}
                        style={{ textDecoration: 'none', minWidth: '0px' }}
                        className="btn btn-link btn-sm"
                      >
                        {t('MenuList.Button.select_all')}
                      </button>
                      /
                      <button
                        type="button"
                        onClick={handleUnSelectedAll}
                        style={{ textDecoration: 'none', minWidth: '0px' }}
                        className="btn btn-link btn-sm"
                      >
                        {t('MenuList.Button.cancel_all')}
                      </button>
                      <span className="ps-2 pe-2"></span>
                      <button type="submit" className="btn-primary btn-circle">
                        <TfiReload />
                      </button>
                      {t('MenuList.Button.reload')}
                    </div>
                  </Form>
                </Col>
                <Col sm={10} lg={10} className="d-flex justify-content-end">
                  <ButtonEditMenu ref={buttonEditRef} proceed={handleOpenMenuDetailModal} />
                  <span className="ps-2 pe-2"></span>
                  <ButtonDeleteMenu
                    proceed={() => {
                      setConfirmDeleteMenu(true);
                    }}
                    ref={buttonDeleteRef}
                  />
                  <span className="ps-2 pe-2"></span>
                  <button type="button" onClick={handleExportCsv} className="btn btn-success btn-wrap">
                    <span className="d-inline-block align-middle">{t('MenuList.Button.export_csv')}</span>
                  </button>
                  <span className="ps-2 pe-2"></span>
                  <button type="button" className="btn btn-success" onClick={handleImportCsvBtn}>
                    {t('MenuList.Button.import_csv')}
                  </button>
                </Col>
              </Row>
              <MenuListTable
                menuDetails={listOfMenuDetail}
                handleCheckBox={handleCheckedMenu}
                checkboxesRef={checkboxesRef}
                handleRestore={handleRestoreMenu}
              />
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

export default MenuList;
